import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { HashLink } from 'react-router-hash-link';
import { injectUrlVars } from 'Utils/Validators';
import { isValidTarget } from 'Utils';

class Link extends React.Component {
  static propTypes = {
    additionalProps: PropTypes.object,
    ariaLabel: PropTypes.string,
    ariaLabelledby: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    e2e: PropTypes.string,
    forwardedRef: PropTypes.object,
    isExternal: PropTypes.bool,
    label: PropTypes.string,
    onClick: PropTypes.func,
    onMouseOut: PropTypes.func,
    onMouseOver: PropTypes.func,
    rel: PropTypes.string,
    screenReaderLabel: PropTypes.string,
    target: PropTypes.string,
    title: PropTypes.string,
    to: PropTypes.string,
    trackingAction: PropTypes.string,
    trackingLabel: PropTypes.string,
  };

  static defaultProps = {
    isExternal: false,
    label: '',
    to: '',
  };

  renderAccessibleLabel = (label, options) => {
    const { target } = options;

    if (label) {
      // ensure space in front of the label which prevents collapsing with the visible button label
      return <span className="sr-only">&nbsp;{label}</span>;
    }

    if (target && target === '_blank') {
      // ensure space in front of the label which prevents collapsing with the visible button label
      return <span className="sr-only">&nbsp;- öffnet in neuem Tab oder Fenster</span>;
    }
  };

  render() {
    const {
      additionalProps,
      ariaLabel,
      ariaLabelledby,
      children,
      className,
      e2e,
      isExternal,
      label,
      onClick,
      onMouseOut,
      onMouseOver,
      forwardedRef,
      rel,
      screenReaderLabel,
      target,
      title,
      to,
      trackingAction,
      trackingLabel,
    } = this.props;

    let propRel = rel;
    if (!propRel && isExternal) {
      propRel = 'noopener noreferrer';
    }

    let propDataTrackingLabel = trackingLabel;
    if (!propDataTrackingLabel && label !== '') {
      propDataTrackingLabel = label.toLowerCase();
    }

    const basicProps = {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      className: className,
      'data-e2e': e2e,
      'data-track-action': trackingAction,
      'data-tracking-label': propDataTrackingLabel,
      onClick: onClick,
      onMouseOut: onMouseOut,
      onMouseOver: onMouseOver,
      ref: forwardedRef,
      rel: propRel,
      target: isValidTarget(target) ? target : undefined,
      title: title,
    };

    const content = (
      <>
        {children ? children : label}
        {!ariaLabel && this.renderAccessibleLabel(screenReaderLabel, basicProps)}
      </>
    );

    if (isExternal) {
      return (
        <a {...basicProps} href={injectUrlVars(to)} {...additionalProps}>
          {content}
        </a>
      );
    }

    return (
      <HashLink {...basicProps} smooth={true} to={injectUrlVars(to)} {...additionalProps}>
        {content}
      </HashLink>
    );
  }
}

export default forwardRef((props, ref) => <Link {...props} forwardedRef={ref} />);
